import { useState, type FormEvent } from 'react';
import type { ClaimInput } from '../../../identity/api';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import { CLAIM_CATALOG, CLAIM_PRESETS, claimKey, sameClaim } from '../../../identity/claimCatalog';

/**
 * Pick tenant claims from the known catalog (one-click presets, chips per service) or type a custom one.
 * `value` = the claims held; `onAdd` gets the new ones only. Without `onRemove` a held chip cannot be unselected
 * (the Claims tab removes through its table, with a confirm).
 */
export function ClaimPicker({
  value,
  onAdd,
  onRemove,
  disabled,
}: {
  value: ClaimInput[];
  onAdd: (claims: ClaimInput[]) => void | Promise<void>;
  onRemove?: (claim: ClaimInput) => void;
  disabled?: boolean;
}) {
  const has = (c: ClaimInput) => value.some((x) => sameClaim(x, c));
  const missing = (cs: ClaimInput[]) => cs.filter((c) => !has(c));
  const toggle = (c: ClaimInput) => {
    if (!has(c)) void onAdd([{ type: c.type, value: c.value }]);
    else onRemove?.(c);
  };
  return (
    <div className="oph-claim-picker" data-testid="claim-picker">
      <div className="oph-claim-group">
        <span className="nyk-ui-hint">Presets</span>
        <div className="nyk-chips">
          {CLAIM_PRESETS.map((p) => {
            const add = missing(p.claims);
            return (
              <Button
                key={p.id}
                size="sm"
                variant="secondary"
                title={`${p.hint}: ${p.claims.map((c) => c.value).join(', ')}`}
                disabled={disabled || !add.length}
                onClick={() => void onAdd(add)}
              >
                + {p.label}
              </Button>
            );
          })}
        </div>
      </div>
      {CLAIM_CATALOG.map((g) => (
        <div key={g.id} className="oph-claim-group">
          <span className="nyk-ui-hint">{g.label}</span>
          <div className="nyk-chips">
            {g.claims.map((c) => {
              const held = has(c);
              return (
                <Chip
                  key={claimKey(c)}
                  size="sm"
                  selected={held}
                  title={c.hint}
                  disabled={disabled || (held && !onRemove)}
                  onClick={() => toggle(c)}
                >
                  {c.type === 'scope' ? c.value : `${c.type}: ${c.value}`}
                </Chip>
              );
            })}
          </div>
        </div>
      ))}
      <CustomClaim disabled={disabled} onAdd={(c) => (has(c) ? undefined : onAdd([c]))} />
    </div>
  );
}

/** Any claim AUTH should store: scope, role or another type (only scope and role reach tokens today). */
function CustomClaim({
  disabled,
  onAdd,
}: {
  disabled?: boolean;
  onAdd: (c: ClaimInput) => void | Promise<void>;
}) {
  const [type, setType] = useState<'scope' | 'role' | 'custom'>('scope');
  const [customType, setCustomType] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const effectiveType = type === 'custom' ? customType.trim() : type;
  const ok = !!effectiveType && !!value.trim();
  // Not a <form>: the picker also sits inside other forms (new-tenant dialog).
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ok) return;
    setBusy(true);
    try {
      await onAdd({ type: effectiveType, value: value.trim() });
      setValue('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="oph-claim-group" data-testid="claim-custom">
      <span className="nyk-ui-hint">Custom</span>
      <div className="nyk-inline-form">
        <Select
          label="Type"
          value={type}
          onChange={(v) => setType(v as typeof type)}
          options={[
            { value: 'scope', label: 'scope' },
            { value: 'role', label: 'role' },
            { value: 'custom', label: 'other…' },
          ]}
          disabled={disabled}
        />
        {type === 'custom' && (
          <TextField
            label="Custom type"
            value={customType}
            onChange={(e) => setCustomType(e.target.value)}
            disabled={disabled}
          />
        )}
        <TextField
          label="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit(e);
          }}
          placeholder={type === 'role' ? 'admin' : 'service-a:items:read'}
          disabled={disabled}
        />
        <Button type="button" onClick={() => void submit()} loading={busy} disabled={disabled || !ok}>
          Add
        </Button>
      </div>
    </div>
  );
}
